/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import type {
  BannerApiResponse,
  CountryBannerItem,
  SnapBannerItem,
} from '../types/banner';
import {
  SNAP_BANNER_DEFAULT_KEY,
  SNAP_BANNER_HEIGHT,
} from '../configs/constants';

const DEFAULT_BANNER_IMAGE = require('../assets/images/banner-placeholder.png');

export const getDefaultBanner = (): SnapBannerItem => ({
  id: 'default-banner',
  name: 'Default Banner',
  url: DEFAULT_BANNER_IMAGE,
});

/**
 * Extract banner name from URL (matching Hotel pattern)
 * @param url - Banner image URL
 */
export const getBannerNameFromUrl = (url: string | number): string => {
  if (typeof url !== 'string') return '';
  const parts = url.split('/');
  const filename = parts[parts.length - 1];
  return filename?.split('.')?.[0] || '';
};

export const sanitizeJsonString = (jsonString: string): string => {
  try {
    JSON.parse(jsonString);
    return jsonString;
  } catch {
    const objects: string[] = [];
    const regex = /\{[^{}]*(?:\{[^{}]*\}[^{}]*)*\}/g;
    let match;
    while ((match = regex.exec(jsonString)) !== null) {
      try {
        JSON.parse(match[0]);
        objects.push(match[0]);
      } catch {
        // skip malformed object
      }
    }
    return objects.length > 0 ? `[${objects.join(',')}]` : jsonString;
  }
};

export const parseJsonArray = <T>(jsonString: string): T[] => {
  try {
    const sanitized = sanitizeJsonString(jsonString);
    return JSON.parse(sanitized);
  } catch {
    return [];
  }
};

export const isBannerActive = (startDate: string, endDate: string): boolean => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T23:59:59.999`);

  return today >= start && today <= end;
};

export const transformBannerData = (
  banners: CountryBannerItem[],
  validateDates = true,
): SnapBannerItem[] => {
  const filteredBanners = banners.filter((banner) => {
    if (!banner.bannerUrl) return false;
    if (!validateDates) return true;
    if (!banner.startDate || !banner.endDate) return false;
    return isBannerActive(banner.startDate, banner.endDate);
  });

  return filteredBanners.map((banner, index) => {
    const bannerName = getBannerNameFromUrl(banner.bannerUrl);
    return {
      id: `${bannerName}-${index}`,
      name: bannerName,
      url: banner.bannerUrl,
      height: SNAP_BANNER_HEIGHT,
      width: undefined,
      redirectionUrl: banner.redirectionUrl,
    };
  });
};

export const parseBannerData = (
  bannerData: BannerApiResponse | undefined,
  countryCode: string | undefined,
  isCountryBannerEnabled: boolean,
): SnapBannerItem[] => {
  const banner = bannerData?.banner;
  if (!banner) return [];

  if (isCountryBannerEnabled && countryCode) {
    const normalizedCountryCode = countryCode.trim();
    const countryBannerData =
      banner[normalizedCountryCode] ??
      banner[normalizedCountryCode.toLowerCase()] ??
      banner[normalizedCountryCode.toUpperCase()];
    if (countryBannerData) {
      const parsedCountryBanners = Array.isArray(countryBannerData)
        ? (countryBannerData as CountryBannerItem[])
        : parseJsonArray<CountryBannerItem>(countryBannerData);
      const activeBanners = transformBannerData(parsedCountryBanners, true);
      if (activeBanners.length > 0) return activeBanners;
    }
  }

  const defaultBannerData = banner[SNAP_BANNER_DEFAULT_KEY];
  return defaultBannerData
    ? transformBannerData(
        Array.isArray(defaultBannerData)
          ? (defaultBannerData as CountryBannerItem[])
          : parseJsonArray<CountryBannerItem>(defaultBannerData),
        false,
      )
    : [];
};
